import React, { useState, useMemo } from "react";
import { FinanceService } from "../services/financeService";
import {
  getMonthsInRange,
  getRelativeMonth,
  formatCurrency,
  formatMonthYearDisplay,
} from "../utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { Category, FinancialEntry } from "@/types/finance";
import { MonthYear } from "@/types/monthYear";

interface Props {
  categories: Category[];
  monthlyValues: FinancialEntry[]; // This should already be filtered by currentSheetId by the parent
}

const ChartsContent: React.FC<Props> = ({ categories, monthlyValues }) => {
  const [startDate, setStartDate] = useState<MonthYear>(() =>
    getRelativeMonth(-6)
  );
  const [endDate, setEndDate] = useState<MonthYear>(() => getRelativeMonth(3));

  const chartData = useMemo(() => {
    const months = getMonthsInRange(startDate, endDate);
    // Note: monthlyValues is already filtered by currentSheetId from MainScreen
    const currentSheetId = monthlyValues[0]?.sheet_id || "default-sheet";

    return months.map((month) => {
      const income = categories
        .filter((c) => !c.parentId && c.type === "income")
        .reduce(
          (acc, c) =>
            acc +
            FinanceService.calculateCategorySum(
              c.id,
              categories,
              monthlyValues,
              month,
              currentSheetId
            ),
          0
        );

      const expense = categories
        .filter((c) => !c.parentId && c.type === "expense")
        .reduce(
          (acc, c) =>
            acc +
            FinanceService.calculateCategorySum(
              c.id,
              categories,
              monthlyValues,
              month,
              currentSheetId
            ),
          0
        );

      return {
        month: formatMonthYearDisplay(month),
        Entradas: income,
        Saídas: expense,
        Saldo: income - expense,
      };
    });
  }, [categories, monthlyValues, startDate, endDate]);

  const stats = useMemo(() => {
    const totalIncome = chartData.reduce((acc, d) => acc + d.Entradas, 0);
    const totalExpense = chartData.reduce((acc, d) => acc + d.Saídas, 0);
    const avgMonthlyExp = totalExpense / (chartData.length || 1);

    return {
      totalIncome,
      totalExpense,
      balance: totalIncome - totalExpense,
      avgMonthlyExp,
    };
  }, [chartData]);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-top-2 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-6 rounded-3xl shadow-sm border border-zinc-200">
        <div>
          <h1 className="text-xl font-black text-zinc-900 tracking-tight">
            Análise de Fluxo
          </h1>
          <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest mt-1">
            Visão Comparativa Temporal
          </p>
        </div>

        <div className="flex items-center gap-3 bg-zinc-50 p-2 rounded-2xl border border-zinc-100">
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-zinc-400 uppercase ml-1 mb-1">
              Início
            </span>
            <input
              type="month"
              value={startDate.toString()}
              onChange={(e) =>
                setStartDate(MonthYear.fromString(e.target.value))
              }
              className="bg-white border border-zinc-200 rounded-xl px-3 py-1.5 text-xs font-bold text-zinc-700 outline-none"
            />
          </div>
          <div className="self-end pb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-zinc-300"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-zinc-400 uppercase ml-1 mb-1">
              Fim
            </span>
            <input
              type="month"
              value={endDate.toString()}
              onChange={(e) => setEndDate(MonthYear.fromString(e.target.value))}
              className="bg-white border border-zinc-200 rounded-xl px-3 py-1.5 text-xs font-bold text-zinc-700 outline-none"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
          <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-1">
            Total Período
          </span>
          <p
            className={`text-lg font-black tracking-tight ${
              stats.balance >= 0 ? "text-emerald-600" : "text-rose-600"
            }`}
          >
            {formatCurrency(stats.balance)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
          <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-1">
            Média Saída
          </span>
          <p className="text-lg font-black text-rose-600 tracking-tight">
            {formatCurrency(stats.avgMonthlyExp)}
          </p>
        </div>
        <div className="hidden md:block bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
          <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-1">
            Meses Ativos
          </span>
          <p className="text-lg font-black text-zinc-800 tracking-tight">
            {chartData.length}
          </p>
        </div>
        <div className="hidden md:block bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
          <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block mb-1">
            Maior Entrada
          </span>
          <p className="text-lg font-black text-emerald-600 tracking-tight">
            {formatCurrency(Math.max(...chartData.map((d) => d.Entradas), 0))}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 md:p-8 rounded-[40px] border border-zinc-200 shadow-sm h-[450px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f4f4f5"
              />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#a1a1aa", fontSize: 9, fontWeight: 700 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#a1a1aa", fontSize: 9, fontWeight: 700 }}
                tickFormatter={(value) =>
                  `R$${value >= 1000 ? value / 1000 + "k" : value}`
                }
              />
              <Tooltip
                cursor={{ fill: "#f8fafc" }}
                contentStyle={{
                  borderRadius: "16px",
                  border: "none",
                  boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                }}
                formatter={(value: number) => [formatCurrency(value), ""]}
              />
              <Bar
                dataKey="Entradas"
                fill="#3f83f8"
                radius={[4, 4, 0, 0]}
                barSize={24}
              />
              <Bar
                dataKey="Saídas"
                fill="#f43f5e"
                radius={[4, 4, 0, 0]}
                barSize={24}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 md:p-8 rounded-[40px] border border-zinc-200 shadow-sm h-[450px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3f83f8" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#3f83f8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f4f4f5"
              />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#a1a1aa", fontSize: 9, fontWeight: 700 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#a1a1aa", fontSize: 9, fontWeight: 700 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "16px",
                  border: "none",
                  boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                }}
                formatter={(value: number) => [formatCurrency(value), "Saldo"]}
              />
              <Area
                type="monotone"
                dataKey="Saldo"
                stroke="#3f83f8"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorSaldo)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ChartsContent;
